import { currentLang, DEFAULT_LANG, isLang, type Lang } from "@/i18n";
import { loadDictionary } from "@/i18n/load";
import { storage } from "@/lib/storage";

export const LANG_STORAGE_KEY = "erp.lang";
/** Set while a language picked on the sign-in page still has to reach the profile. */
export const LANG_CHOSEN_KEY = "erp.lang.chosen";

export function storedLanguage(): Lang | null {
  const v = storage.get(LANG_STORAGE_KEY);
  return isLang(v) ? v : null;
}

/** The browser's saved choice, else English: before sign-in there is no profile to ask. */
export function initialLanguage(): Lang {
  return storedLanguage() ?? DEFAULT_LANG;
}

/** Installs the dictionary of the language to start in and marks <html lang>. */
export async function bootLanguage(): Promise<Lang> {
  const lang = initialLanguage();
  await loadDictionary(lang);
  document.documentElement.lang = lang;
  return lang;
}

/** Saves lang for the next load; false when this browser cannot keep it. */
export function persistLanguage(lang: Lang): boolean {
  storage.set(LANG_STORAGE_KEY, lang);
  return storage.get(LANG_STORAGE_KEY) === lang;
}

/** Marks the running language as picked by hand before sign-in (it then wins over the profile). */
export function markLanguageChosen(): void {
  storage.set(LANG_CHOSEN_KEY, "1");
}

export function languageChosen(): boolean {
  return storage.get(LANG_CHOSEN_KEY) === "1";
}

export function clearLanguageChosen(): void {
  storage.remove(LANG_CHOSEN_KEY);
}

/**
 * After sign-in: when the profile's language differs from the running one,
 * save it and reload once. A browser that cannot store it is left in the
 * running language rather than reloaded forever; a value that is not a
 * language is ignored.
 */
export function adoptProfileLanguage(lang: Lang | undefined, reload: () => void = () => window.location.reload()): boolean {
  if (!isLang(lang) || lang === currentLang()) return false;
  if (!persistLanguage(lang)) return false;
  reload();
  return true;
}

/**
 * What the page shows when the dictionary or the app itself failed to load
 * (offline, a deploy replaced the chunks). No dictionary is available here, so
 * the text is in both languages.
 */
export function renderBootFailure(root: HTMLElement, reload: () => void = () => window.location.reload()): void {
  const box = document.createElement("div");
  box.setAttribute("role", "alert");
  box.style.cssText = "max-width:28rem;margin:4rem auto;padding:0 1rem;font-family:system-ui,sans-serif;text-align:center";
  const message = document.createElement("p");
  message.textContent = "The app could not be loaded. Check your connection and try again.";
  const messageId = document.createElement("p");
  messageId.lang = "id";
  messageId.textContent = "Aplikasi tidak dapat dimuat. Periksa koneksi Anda lalu coba lagi.";
  const retry = document.createElement("button");
  retry.type = "button";
  const retryId = document.createElement("span");
  retryId.lang = "id";
  retryId.textContent = "Coba lagi";
  retry.append("Try again / ", retryId);
  retry.style.cssText = "margin-top:1rem;padding:0.5rem 1rem";
  retry.addEventListener("click", () => reload());
  box.append(message, messageId, retry);
  root.replaceChildren(box);
  // The page has nothing else to offer: put the keyboard on the way out.
  retry.focus();
}
